import { Col, Container, Row } from "react-bootstrap";
import React, { ReactNode } from "react";
import CustomButton from "shared/components/button/CustomButton";
import CustomModal from "shared/components/modal/CustomModal";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { CustomTimePicker } from "shared/components/date/CustomTimePicker";
import { useGestaoCobrancaCadastroContext } from "context/gestaoCobranca/GestaoCobrancaCadastroContext";
import { RegistroUsuarioDTO } from "types/dto/RegistroUsuarioDTO";
import { SelectOptionType } from "types/select/SelectOptionType";
import SelectAsync from "shared/components/select/SelectAsync";
import { api } from "constants/RotasAPI";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AgendarContatoGestaoCobrancaCadastroSchema } from "shared/yup/schemas/AgendarContatoGestaoCobrancaCadastroSchema";
import { TipoSacado } from "constants/EnumTipoSacado";
import { formatarDateParaLocalTime } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { AgendamentoContatoDTO } from "types/dto/AgendamentoContatoDTO";
import { usePostAgendarContato } from "hooks/agendamentoContato/usePostAgendarContatos";
import { toast } from "react-hot-toast";
import showAlert from "shared/util/functions/alert/showAlert";

interface ModalAgendamentoProps {
  showModal: boolean;
  onCloseModal: () => void;
}

interface AgendamentoForm {
  responsavelAgendamento: SelectOptionType<RegistroUsuarioDTO> | null;
  dataAgendamento: Date | null;
  horarioAgendamento: Date;
}

const ModalAgendamento: React.FC<ModalAgendamentoProps> = (props) => {
  const { unidadeId } = useGestaoCobrancaCadastroContext();
  const { register, handleSubmit, reset, control, formState, setValue } = useForm<AgendamentoForm>({
    resolver: yupResolver<AgendamentoForm>(AgendarContatoGestaoCobrancaCadastroSchema),
  });
  const { mutateAsync: postAgendarContato } = usePostAgendarContato();

  const renderFooterModalAdicionarContato = (): ReactNode => {
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-agendamento"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-cancelar-modal-agendamento"
            label="Salvar"
            onClick={handleSubmit(agendarContato)}
            variant="primary"
            className="ps-4 pe-4"
          />
        </Col>
      </Row>
    );
  };
  const agendarContato = async (form: AgendamentoForm) => {
    const agendamentoContatoDTO = {
      dataAgendamento: form.dataAgendamento?.toISOString(),
      horarioAgendamento: formatarDateParaLocalTime(form.horarioAgendamento),
      unidadeId: unidadeId,
      tipoSacado: TipoSacado.PROPRIETARIO,
      nomeResponsavel: form.responsavelAgendamento?.value?.nome,
      emailResponsavel: form.responsavelAgendamento?.value?.email,
    } as AgendamentoContatoDTO;

    try {
      await postAgendarContato(agendamentoContatoDTO);
      reset();
      props.onCloseModal();
    } catch (error: any) {
      if (error?.response?.data?.validation[0]?.message) {
        toast.error(error.response.data.validation[0].message);
      } else {
        toast.error("Erro ao salvar registro.");
      }
    }
  };

  const renderBodyModalAgendamento = (): ReactNode => {
    return (
      <Container>
        <Row className="mb-2">
          <span>Realize o agendamento de contato com a unidade</span>
        </Row>

        <Row className="mb-3">{renderResponsavelAgendamento()}</Row>

        <Row className="mb-3">
          {renderDataAgendamento()}
          {renderHorarioAgendamento()}
        </Row>
      </Container>
    );
  };

  const renderResponsavelAgendamento = (): ReactNode => {
    return (
      <Col xl={10} sm={10} xs={10}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="responsavel-agendamento"
          name="responsavelAgendamento"
          label="Responsável pelo agendamento"
          endpoint={`${api.usuario.getRegistrosUsuarios}`}
          control={control}
          setValue={setValue}
          register={register}
          nomeAtributoLabel="nomeUsuario"
          nomeAtributoValue="nomeEmailUsuario"
        />
      </Col>
    );
  };
  const renderDataAgendamento = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomDatePicker
          usarValidacaoParaFormulario
          name="dataAgendamento"
          control={control}
          register={register}
          setValue={setValue}
          label="Data do agendamento"
          disabledPast
        />
      </Col>
    );
  };

  const renderHorarioAgendamento = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomTimePicker
          usarValidacaoParaFormulario
          label="Horário do agendamento"
          name="horarioAgendamento"
          control={control}
          register={register}
          setValue={setValue}
        />
      </Col>
    );
  };

  const closeModal = () => {
    const fecharModal = () => {
      reset();
      props.onCloseModal();
    };
    if (formState.isDirty) {
      showAlert({
        texto:
          "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      fecharModal();
    }
  };

  return (
    <CustomModal
      titulo="Agendamento"
      onCloseModal={() => closeModal()}
      customFooter={renderFooterModalAdicionarContato()}
      showModal={props.showModal}
      size="xl"
      tipoModal="MODAL_LATERAL_METADE"
      fecharModalAoClicarFora={!formState.isDirty}
      scrollable
    >
      {renderBodyModalAgendamento()}
    </CustomModal>
  );
};

export default ModalAgendamento;
